import { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { UiKitSliderBase } from '@/fo/ui/kit/slider/base';

import { FilterOptionRange } from '../../../types';
import { FilterComponentProps } from '../types';

import { styles } from './RangeFilter.styles';

/** Компонент для отображения range фильтров */
export function RangeFilter({
  options,
  selected,
  onChange
}: FilterComponentProps) {
  const rangeOptions = options as FilterOptionRange;
  const slots = styles();

  const minPrice = rangeOptions?.from ?? 0;
  const maxPrice = rangeOptions?.to ?? 0;
  const isDisabled = minPrice >= maxPrice;

  const clamp = (val: number) => Math.max(minPrice, Math.min(maxPrice, val));

  const [internalValue, setInternalValue] = useState<[number, number]>(() => {
    if (!Array.isArray(selected) || selected.length !== 2) {
      return [minPrice, maxPrice];
    }
    return [clamp(+selected[0]), clamp(+selected[1])];
  });

  const debouncedOnChange = useDebouncedCallback(
    (value: [number, number]) => onChange(value),
    300
  );

  useEffect(() => {
    if (!Array.isArray(selected) || selected.length !== 2) {
      setInternalValue([minPrice, maxPrice]);
      return;
    }

    const newValue: [number, number] = [
      Math.max(minPrice, Math.min(maxPrice, +selected[0])),
      Math.max(minPrice, Math.min(maxPrice, +selected[1]))
    ];

    setInternalValue((prev) =>
      prev[0] === newValue[0] && prev[1] === newValue[1] ? prev : newValue
    );
  }, [selected, minPrice, maxPrice]);

  useEffect(() => () => debouncedOnChange.cancel(), [debouncedOnChange]);

  if (!rangeOptions) return null;

  const handleChange = (value: [number, number]) => {
    if (isDisabled) return;

    const normalized: [number, number] = [clamp(value[0]), clamp(value[1])];

    setInternalValue(normalized);
    debouncedOnChange(normalized);
  };

  const offset = Math.max(1, Math.abs(minPrice) * 0.01);
  const sliderMin = isDisabled ? minPrice - offset : minPrice;
  const sliderMax = isDisabled ? maxPrice + offset : maxPrice;
  const sliderValue: [number, number] = isDisabled
    ? [sliderMin, sliderMax]
    : internalValue;

  return (
    <div className={slots.base()}>
      <UiKitSliderBase
        min={sliderMin}
        max={sliderMax}
        value={sliderValue}
        onChange={handleChange}
        labelFrom={String(isDisabled ? minPrice : internalValue[0])}
        labelTo={String(isDisabled ? maxPrice : internalValue[1])}
        disabled={isDisabled}
      />
    </div>
  );
}
